import { useTranslation } from 'react-i18next';

import { formatNumber } from '@/entities/dashboard';
import type { ConsignmentListType } from '@/entities/dashboard/mapping';

import { useClusterConsignments } from '../model/use-cluster-consignments';
import type { MapDataFilters } from '../model/map-provider.interface';

interface ClusterConsignmentsProps {
  locationIds: string[];
  type: ConsignmentListType;
  weeksAgo: number;
  dataFilters?: MapDataFilters;
}

/**
 * Legacy prints the unit in the column head. Some locales already carry it in the
 * label itself, so appending unconditionally would read "Poids (kg) (kg)".
 */
const columnHead = (label: string, unit: string) => (label.includes('(') ? label : `${label} (${unit})`);

/**
 * Head and rows join one grid through `subgrid`, so each column is sized once, as in
 * the legacy table. Rows sized as grids of their own lost alignment whenever a larger
 * browser font squeezed the sender column to each row's own longest word.
 */
const ROW = 'col-span-full grid grid-cols-subgrid';

export function ClusterConsignments({ locationIds, type, weeksAgo, dataFilters }: ClusterConsignmentsProps) {
  const { t } = useTranslation();
  const { consignments, isLoading } = useClusterConsignments({ locationIds, type, weeksAgo, dataFilters });

  if (isLoading) {
    return <p className="py-2 text-center text-muted-foreground text-sm">{t('common.loading')}</p>;
  }

  if (consignments.length === 0) {
    return <p className="py-2 text-center text-muted-foreground text-sm">{t('common.noData')}</p>;
  }

  return (
    <div className="grid max-h-52 grid-cols-[auto_minmax(35%,1fr)_auto_auto] gap-x-2 overflow-y-auto text-xs">
      <div className={`${ROW} sticky top-0 border-neutral-alpha-300 border-b bg-white pb-1 text-muted-foreground`}>
        <span>{t('request.cmrNumber.short')}</span>
        <span>{t('request.sender.label')}</span>
        <span className="text-right">{columnHead(t('requestline.ltuWeight.label'), 'kg')}</span>
        <span className="text-right">{columnHead(t('requestline.ltuVolume.label'), 'm³')}</span>
      </div>

      {consignments.map((consignment, index) => (
        <div
          key={`${consignment.consignmentNumber}-${index}`}
          className={`${ROW} border-border py-1 ${index > 0 ? 'border-t' : ''}`}>
          <span>{consignment.consignmentNumber}</span>
          <span className="break-words">{consignment.senderOrganization}</span>
          <span className="text-right whitespace-nowrap">{formatNumber(consignment.ltuWeight)}</span>
          <span className="text-right whitespace-nowrap">{formatNumber(consignment.ltuVolume, 2)}</span>
        </div>
      ))}
    </div>
  );
}
